/**
 * Theme customizations (keeps theme.js untouched):
 * - Slider arrow centering on media
 * - Collection / products / reviews stagger reveal
 * - Judge.me badge formatting
 * - Collection highlight + hero slider tabs
 * - Blog tags toggle
 */

import { animate, stagger, inView } from "vendor";

document.addEventListener(
  "pointerup",
  (event) => {
    const button = event.target.closest(".prev-next-button");
    if (!button || event.pointerType === "mouse") return;
    button.blur();
  },
  true
);

document.addEventListener(
  "click",
  (event) => {
    const path = event.composedPath?.() || [];
    const clickedSelleasyButton = path.some((item) => {
      if (!(item instanceof Element)) return false;
      return item.matches(".lb-button") || Boolean(item.closest(".lb-button"));
    });

    if (clickedSelleasyButton) {
      const initialCount = Number(document.querySelector("cart-count")?.textContent?.trim());

      const refreshAndOpenCartDrawer = () => {
        if (isCartPageView()) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        const cartDrawer = document.getElementById("cart-drawer");
        if (!cartDrawer) return;

        const openCartDrawer = () => {
          if (typeof cartDrawer.show === "function") {
            cartDrawer.show();
          } else {
            document.querySelector('[aria-controls="cart-drawer"]')?.click();
          }
        };

        const fallbackTimeout = window.setTimeout(openCartDrawer, 900);

        cartDrawer.addEventListener(
          "cart-drawer:refreshed",
          () => {
            window.clearTimeout(fallbackTimeout);
            openCartDrawer();
          },
          { once: true }
        );

        document.dispatchEvent(new CustomEvent("cart:refresh"));
      };

      let attempts = 0;
      const intervalId = window.setInterval(async () => {
        attempts += 1;

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cart = await response.json();

          if (!Number.isFinite(initialCount) || cart.item_count !== initialCount || attempts >= 12) {
            window.clearInterval(intervalId);
            window.setTimeout(refreshAndOpenCartDrawer, 5000);
          }
        } catch (error) {
          if (attempts >= 12) {
            window.clearInterval(intervalId);
            window.setTimeout(refreshAndOpenCartDrawer, 5000);
          }
        }
      }, 500);
    }
  },
  true
);

function findAutoCartActionControl(event) {
  const path = event.composedPath?.() || [];

  for (const item of path) {
    if (!(item instanceof Element)) continue;

    const control = item.matches(".aca-cco") ? item : item.closest(".aca-cco");
    if (control) return control;
  }

  return null;
}

document.addEventListener(
  "click",
  async (event) => {
    const control = findAutoCartActionControl(event);
    if (!control) return;

    const line = Number(control.getAttribute("aca-line"));
    const quantity = Number(control.getAttribute("aca-quantity"));
    if (!Number.isFinite(line) || !Number.isFinite(quantity)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const sections = bundleCartSections();
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          line,
          quantity,
          sections: sections.join(","),
          sections_url: window.location.pathname
        })
      });

      await updateCartFromResponse(response, { baseEvent: "line-item:change" });
      if (isCartPageView()) {
        window.location.reload();
        return;
      }
      document.getElementById("cart-drawer")?.show?.();
    } catch (error) {
      showCartQtyAlert(control);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  },
  true
);

function isQuantitySelectorInput(target) {
  return target instanceof HTMLInputElement && target.classList.contains("quantity-selector__input");
}

document.addEventListener(
  "keydown",
  (event) => {
    if (!isQuantitySelectorInput(event.target)) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length === 1 && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  },
  true
);

document.addEventListener(
  "beforeinput",
  (event) => {
    if (!isQuantitySelectorInput(event.target)) return;
    if (event.data == null) return;
    if (!/^\d+$/.test(event.data)) event.preventDefault();
  },
  true
);

document.addEventListener(
  "paste",
  (event) => {
    if (!isQuantitySelectorInput(event.target)) return;
    const digits = (event.clipboardData?.getData("text") || "").replace(/\D/g, "");
    event.preventDefault();
    if (!digits) return;

    const input = event.target;
    input.value = digits;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  },
  true
);

document.addEventListener(
  "drop",
  (event) => {
    if (!isQuantitySelectorInput(event.target)) return;
    event.preventDefault();
  },
  true
);

function isMobileViewport() {
  return window.matchMedia("(max-width: 43.75rem)").matches;
}

function scrollNewsletterFormToTop(form) {
  const top = form.getBoundingClientRect().top + window.scrollY - 50;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

document.addEventListener("focusin", (event) => {
  if (!isMobileViewport()) return;

  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (input.type !== "email" && input.name !== "contact[email]") return;

  const section = input.closest(".shopify-section--newsletter");
  const form = input.closest("form");
  if (!section || !form) return;

  requestAnimationFrame(() => {
    scrollNewsletterFormToTop(form);
    window.setTimeout(() => scrollNewsletterFormToTop(form), 350);
  });
});

function syncSliderArrowPosition(slider, mediaSelector, cssVar) {
  const media = slider.querySelector(mediaSelector);
  if (!media) return;

  const top = media.offsetTop + media.offsetHeight / 2;
  slider.style.setProperty(cssVar, `${top}px`);
}

function bindSliderArrowPosition(slider, mediaSelector, cssVar) {
  const update = () => syncSliderArrowPosition(slider, mediaSelector, cssVar);

  if (slider.dataset.arrowSync === "true") {
    update();
    return;
  }

  slider.dataset.arrowSync = "true";
  update();

  const media = slider.querySelector(mediaSelector);
  if (media && "ResizeObserver" in window) {
    new ResizeObserver(update).observe(media);
  }

  window.addEventListener("resize", update);
}

function initThemeSliders(root = document) {
  root.querySelectorAll(".collection-list__slider").forEach((slider) => {
    bindSliderArrowPosition(slider, ".collection-card__media", "--collection-list-arrow-top");
  });

  root.querySelectorAll(".products-from-collection__slider").forEach((slider) => {
    bindSliderArrowPosition(slider, ".product-card__figure", "--products-from-collection-arrow-top");
  });

  root.querySelectorAll(".reviews-slider__slider").forEach((slider) => {
    bindSliderArrowPosition(slider, ".reviews-slider__card", "--reviews-slider-arrow-top");
  });
}

customElements.whenDefined("scroll-carousel").then(() => {
  initThemeSliders();
});

function prefersMotion() {
  return window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
}

function bindListReveal(root, listSelector, itemSelector) {
  if (!prefersMotion()) {
    root.querySelectorAll(listSelector).forEach((list) => {
      list.style.opacity = "1";
      list.querySelectorAll(itemSelector).forEach((item) => {
        item.style.opacity = "1";
      });
    });
    return;
  }

  root.querySelectorAll(listSelector).forEach((list) => {
    if (list.dataset.revealReady === "true") return;
    list.dataset.revealReady = "true";

    inView(
      list,
      () => {
        if (list.hasAttribute("reveal-on-scroll")) list.style.opacity = "1";
        const items = list.querySelectorAll(itemSelector);
        if (!items.length) return;

        animate(
          items,
          { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] },
          { duration: 0.25, delay: stagger(0.1, { easing: "ease-out" }), easing: "ease" }
        );
      },
      { margin: "-50px 0px" }
    );
  });
}

function initCollectionCardReveal(root = document) {
  bindListReveal(root, ".collection-list.wrap[reveal-on-scroll='true']", ".collection-card");
  bindListReveal(root, ".collection-list[data-stagger-reveal]", ".collection-card");
  bindListReveal(root, ".products-from-collection__list[data-stagger-reveal]", ".products-from-collection__item");
  bindListReveal(root, ".reviews-slider__list[data-stagger-reveal]", ".reviews-slider__card");
}

function formatRatingSummary(average, reviews) {
  if (!Number.isFinite(average) || average <= 0) return null;
  const count = Number.isFinite(reviews) && reviews >= 0 ? reviews : 0;
  return { average: average.toFixed(1), count };
}

function formatJudgemeSummaryBadge(badge) {
  const text = badge.querySelector(".jdgm-prev-badge__text");
  if (!text) return;

  const rating = Number(badge.dataset.averageRating);
  const count = Number(badge.dataset.numberOfReviews);
  const parsed = text.textContent.match(/(\d+(?:\.\d+)?)/g) || [];
  const average = Number.isFinite(rating) && rating > 0 ? rating : Number(parsed[0]);
  const reviews =
    Number.isFinite(count) && count >= 0
      ? count
      : Number(parsed.find((value, index) => index > 0 && Number(value) >= 1)) || 0;

  const summary = formatRatingSummary(average, reviews);
  if (!summary) return;
  text.textContent = `${summary.average} (${summary.count} reviews)`;
}

function formatJudgemeWidgetSummary(widget) {
  const text = widget.querySelector(".jdgm-rev-widg__summary-text");
  if (!text) return;

  const summary = formatRatingSummary(
    Number(widget.dataset.averageRating),
    Number(widget.dataset.numberOfReviews)
  );
  if (!summary) return;
  text.innerHTML = `<span>${summary.average}</span> (${summary.count} reviews)`;
}

function initJudgemeCardBadges(root = document) {
  root.querySelectorAll(".product-card .jdgm-prev-badge").forEach((badge) => {
    const rating = Number(badge.dataset.averageRating);
    const text = badge.querySelector(".jdgm-prev-badge__text");
    if (!text || !Number.isFinite(rating) || rating <= 0) return;
    text.textContent = rating.toFixed(1);
  });

  root.querySelectorAll(".reviews-slider__summary .jdgm-prev-badge").forEach(formatJudgemeSummaryBadge);
  root.querySelectorAll(".jdgm-rev-widg").forEach(formatJudgemeWidgetSummary);
}

function tabKeyNextIndex(event, index, length) {
  if (event.key === "ArrowDown" || event.key === "ArrowRight") return (index + 1) % length;
  if (event.key === "ArrowUp" || event.key === "ArrowLeft") return (index - 1 + length) % length;
  if (event.key === "Home") return 0;
  if (event.key === "End") return length - 1;
  return -1;
}

function bindTabNavigation(root, tabs, tabSelector, getId, activate) {
  root.addEventListener("click", (event) => {
    const tab = event.target.closest(tabSelector);
    if (!tab || !root.contains(tab)) return;
    event.preventDefault();
    activate(root, getId(tab));
  });

  root.addEventListener("keydown", (event) => {
    const tab = event.target.closest(tabSelector);
    if (!tab || !root.contains(tab)) return;

    const index = tabs.indexOf(tab);
    const nextIndex = tabKeyNextIndex(event, index, tabs.length);
    if (nextIndex < 0) return;

    event.preventDefault();
    const next = tabs[nextIndex];
    activate(root, getId(next));
    next.focus();
  });
}

function activateCollectionHighlight(root, id) {
  if (!root || !id) return;

  root.querySelectorAll("[data-highlight-tab]").forEach((tab) => {
    const on = tab.dataset.highlightTab === id;
    tab.classList.toggle("is-active", on);
    tab.setAttribute("aria-selected", on ? "true" : "false");
    tab.tabIndex = on ? 0 : -1;
  });

  root.querySelectorAll("[data-highlight-panel]").forEach((panel) => {
    const on = panel.dataset.highlightPanel === id;
    panel.hidden = !on;
    panel.classList.toggle("is-active", on);
  });

  root.querySelectorAll("[data-highlight-copy]").forEach((copy) => {
    const on = copy.dataset.highlightCopy === id;
    copy.hidden = !on;
    copy.classList.toggle("is-active", on);
  });
}

function initCollectionHighlight(root = document) {
  root.querySelectorAll("[data-collection-highlight]").forEach((section) => {
    if (section.dataset.highlightReady === "true") return;
    section.dataset.highlightReady = "true";

    const tabs = [...section.querySelectorAll("[data-highlight-tab]")];
    if (!tabs.length) return;

    bindTabNavigation(
      section,
      tabs,
      "[data-highlight-tab]",
      (tab) => tab.dataset.highlightTab,
      activateCollectionHighlight
    );
  });
}

function activateHeroSlider(root, id) {
  if (!root || !id) return;

  root.querySelectorAll("[data-hero-tab]").forEach((tab) => {
    const on = tab.dataset.heroTab === id;
    tab.classList.toggle("is-active", on);
    tab.setAttribute("aria-selected", on ? "true" : "false");
    tab.tabIndex = on ? 0 : -1;
    if (on) {
      const scroller = tab.closest(".hero-slider__tabs");
      if (scroller) scroller.scrollTo({ left: Math.max(tab.offsetLeft - 8, 0), behavior: "smooth" });
    }
  });

  root.querySelectorAll("[data-hero-slide]").forEach((slide) => {
    const on = slide.dataset.heroSlide === id;
    slide.hidden = false;
    slide.classList.toggle("is-active", on);
    slide.setAttribute("aria-hidden", on ? "false" : "true");
    slide.querySelectorAll("video").forEach((video) => {
      if (on) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  });
}

function bindHeroSliderSwipe(root, tabs) {
  if (tabs.length < 2) return;

  const threshold = 40;
  let startX = 0;
  let startY = 0;
  let tracking = false;

  const isSwipeIgnore = (target) => Boolean(target.closest(".hero-slider__tabs, a, button"));

  const onStart = (x, y, target) => {
    if (isSwipeIgnore(target)) {
      tracking = false;
      return;
    }
    tracking = true;
    startX = x;
    startY = y;
  };

  const onEnd = (x, y) => {
    if (!tracking) return;
    tracking = false;
    const dx = x - startX;
    const dy = y - startY;
    if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;

    const current = tabs.findIndex((tab) => tab.classList.contains("is-active"));
    const next = dx < 0 ? (current + 1) % tabs.length : (current - 1 + tabs.length) % tabs.length;
    activateHeroSlider(root, tabs[next].dataset.heroTab);
  };

  root.addEventListener(
    "touchstart",
    (event) => {
      const touch = event.changedTouches[0];
      onStart(touch.clientX, touch.clientY, event.target);
    },
    { passive: true }
  );

  root.addEventListener(
    "touchend",
    (event) => {
      const touch = event.changedTouches[0];
      onEnd(touch.clientX, touch.clientY);
    },
    { passive: true }
  );

  root.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "touch") return;
    onStart(event.clientX, event.clientY, event.target);
  });

  root.addEventListener("pointerup", (event) => {
    if (event.pointerType === "touch") return;
    onEnd(event.clientX, event.clientY);
  });
}

function initHeroSlider(root = document) {
  root.querySelectorAll("[data-hero-slider]").forEach((section) => {
    if (section.dataset.heroReady === "true") return;
    section.dataset.heroReady = "true";

    const tabs = [...section.querySelectorAll("[data-hero-tab]")];
    if (!tabs.length) return;

    bindTabNavigation(
      section,
      tabs,
      "[data-hero-tab]",
      (tab) => tab.dataset.heroTab,
      activateHeroSlider
    );

    bindHeroSliderSwipe(section, tabs);
    activateHeroSlider(section, tabs[0].dataset.heroTab);
  });
}

function initBlogTags(root = document) {
  root.querySelectorAll("[data-blog-tags]").forEach((el) => {
    const toggle = el.querySelector("[data-blog-tags-toggle]");
    const panel = el.querySelector("[data-blog-tags-panel]");
    if (!toggle || !panel || toggle.dataset.bound === "true") return;

    toggle.dataset.bound = "true";

    toggle.addEventListener("click", () => {
      const open = panel.hidden;
      el.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      panel.hidden = !open;
    });
  });
}

function barcodeForAboutCollection(variant) {
  if (!variant) return "";

  const fromVariant = String(variant.barcode || "").trim();
  if (fromVariant) return fromVariant;

  const mapEl = document.querySelector("[data-about-collection-isbn-map]");
  if (!mapEl || variant.id == null) return "";

  try {
    const map = JSON.parse(mapEl.textContent);
    return String(map[variant.id] || "").trim();
  } catch {
    return "";
  }
}

function updateAboutCollectionIsbn(variant) {
  const barcode = barcodeForAboutCollection(variant);

  document.querySelectorAll("[data-about-collection-isbn]").forEach((spec) => {
    const valueEl = spec.querySelector("[data-about-collection-isbn-value]");
    if (!barcode) {
      spec.hidden = true;
      if (valueEl) valueEl.textContent = "";
      return;
    }

    spec.hidden = false;
    if (valueEl) valueEl.textContent = barcode;
  });
}

function decodeAboutCollectionSpecText(value) {
  const parsed = new DOMParser().parseFromString(String(value || ""), "text/html");
  const text = parsed.body?.textContent || "";

  return text.replace(/"/g, "″");
}

function mappedAboutCollectionSpec(specId, variant) {
  const mapEl = document.querySelector(`[data-about-collection-shopify-spec-map="${specId}"]`);
  if (!mapEl || !variant || variant.id == null) return "";

  try {
    const map = JSON.parse(mapEl.textContent);
    return String(map[variant.id] || "").trim();
  } catch {
    return "";
  }
}

function updateAboutCollectionShopifySpecs(variant) {
  document.querySelectorAll("[data-about-collection-shopify-spec]").forEach((spec) => {
    const specId = spec.dataset.specId;
    const valueEl = spec.querySelector("[data-about-collection-shopify-spec-value]");
    const value = decodeAboutCollectionSpecText(
      mappedAboutCollectionSpec(specId, variant) || spec.dataset.specFallback || ""
    );

    if (!value) {
      spec.hidden = true;
      if (valueEl) valueEl.textContent = "";
      return;
    }

    spec.hidden = false;
    if (valueEl) valueEl.textContent = value;
  });
}

function initAboutCollectionBooks(root = document) {
  root.querySelectorAll(".about-collection__books-toggle").forEach((toggle) => {
    if (toggle.dataset.bound === "true") return;
    toggle.dataset.bound = "true";

    toggle.addEventListener("click", () => {
      const wrap = toggle.closest(".about-collection__books-wrap");
      if (!wrap) return;

      const expanded = wrap.classList.toggle("is-expanded");
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      toggle.textContent = expanded ? toggle.dataset.labelHide : toggle.dataset.labelShow;
    });
  });
}

function lineItemPrice(item) {
  return Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0);
}

function shippingSubtotalFromCart(cart) {
  if (!cart) return 0;

  const addonVariantId = getCartAddonVariantId();
  const withoutAddon = (items) =>
    (items || []).reduce((sum, item) => {
      if (addonVariantId && Number(item.variant_id) === addonVariantId) return sum;
      return sum + lineItemPrice(item);
    }, 0);

  const cartTotal = Number(cart.total_price ?? 0);
  if (cartTotal > 0) {
    const addon = (cart.items || []).find((item) => addonVariantId && Number(item.variant_id) === addonVariantId);
    return Math.max(0, cartTotal - (addon ? lineItemPrice(addon) : 0));
  }

  const itemsTotal = (cart.items || []).reduce((sum, item) => {
    if (item.requires_shipping === false) return sum;
    if (addonVariantId && Number(item.variant_id) === addonVariantId) return sum;
    return sum + lineItemPrice(item);
  }, 0);

  if (itemsTotal > 0) return itemsTotal;

  return withoutAddon(cart.items);
}

class LbFreeShippingBar extends HTMLElement {
  static lastCart = null;
  static lastScale = null;

  connectedCallback() {
    this.messageEl = this.querySelector("[data-shipping-message]");
    this.goalEl = this.querySelector("[data-shipping-goal]");
    this.trackEl = this.querySelector("[data-shipping-track]");
    this.fillEl = this.querySelector("[data-shipping-fill]");
    this.thresholdCents = Number(this.getAttribute("data-threshold-cents")) || 0;
    this.unreachedMessage = this.getAttribute("unreached-message") || "";
    this.reachedMessage = this.getAttribute("reached-message") || "";
    this.money = new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: Shopify.currency.active,
    });

    this.onCartChange = (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;
      LbFreeShippingBar.lastCart = cart;
      this.render(shippingSubtotalFromCart(cart), { animate: true });
    };

    const liquidTotal = Number(this.getAttribute("data-total-cents")) || 0;
    const lastTotal = LbFreeShippingBar.lastCart ? shippingSubtotalFromCart(LbFreeShippingBar.lastCart) : 0;
    const startTotal = Math.max(liquidTotal, lastTotal);

    if (LbFreeShippingBar.lastScale != null) {
      this.snapScale(LbFreeShippingBar.lastScale);
    }

    if (startTotal > 0) {
      requestAnimationFrame(() => this.render(startTotal, { animate: LbFreeShippingBar.lastScale != null }));
    }

    document.addEventListener("cart:change", this.onCartChange);
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.onCartChange);
    window.removeEventListener("pageshow", this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) this.syncFromCartApi();
  };

  async syncFromCartApi() {
    try {
      const cart = await fetch(`${Shopify.routes.root}cart.js`, { credentials: "same-origin" }).then((response) =>
        response.json()
      );
      if (!this.isConnected) return;
      LbFreeShippingBar.lastCart = cart;
      this.render(shippingSubtotalFromCart(cart), { animate: true });
    } catch (error) {
      /* keep current progress */
    }
  }

  snapScale(scale) {
    if (!this.fillEl) return;
    this.fillEl.style.transition = "none";
    this.fillEl.style.transform = `scaleX(${scale})`;
    this.style.setProperty("--free-shipping-progress", String(scale));
    this.fillEl.getBoundingClientRect();
  }

  render(totalCents, { animate = true } = {}) {
    const threshold = this.thresholdCents;
    const reached = threshold <= 0 || totalCents >= threshold;
    const remainingCents = Math.max(0, threshold - totalCents);
    const percent = reached ? 100 : threshold === 0 ? 100 : Math.max(0, Math.min(100, (totalCents / threshold) * 100));
    const scale = percent / 100;
    const remainingLabel = this.money.format(remainingCents / 100);
    const goalLabel = this.money.format(threshold / 100);
    const message = reached
      ? this.reachedMessage
      : this.unreachedMessage.replace(/\{\{.*?\}\}/g, remainingLabel);

    this.classList.toggle("is-reached", reached);
    this.dataset.totalCents = String(totalCents);

    if (this.messageEl) this.messageEl.textContent = message;
    if (this.goalEl) this.goalEl.textContent = goalLabel;
    if (this.trackEl) {
      this.trackEl.setAttribute("aria-valuenow", String(Math.round(percent)));
      this.trackEl.setAttribute("aria-valuetext", message);
    }

    if (!this.fillEl) return;

    const previous = LbFreeShippingBar.lastScale;
    const unchanged = previous != null && Math.abs(previous - scale) < 0.002;

    if (!animate || previous == null || unchanged) {
      this.snapScale(scale);
      this.fillEl.style.transition = "";
      LbFreeShippingBar.lastScale = scale;
      return;
    }

    this.snapScale(previous);
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.fillEl) return;
      this.fillEl.style.transition = "";
      this.fillEl.style.transform = `scaleX(${scale})`;
      this.style.setProperty("--free-shipping-progress", String(scale));
      LbFreeShippingBar.lastScale = scale;
    });
  }
}

document.addEventListener("cart:change", (event) => {
  if (event.detail?.cart) LbFreeShippingBar.lastCart = event.detail.cart;
});

if (!customElements.get("lb-free-shipping-bar")) {
  customElements.define("lb-free-shipping-bar", LbFreeShippingBar);
}

function handleizeType(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isCartPageView() {
  return Boolean(document.querySelector(".shopify-section--main-cart"));
}

function getCartAddonVariantId() {
  return Number(
    document.querySelector("cart-page")?.dataset.cartAddonVariant ||
      document.getElementById("cart-drawer")?.dataset.cartAddonVariant
  );
}

const CART_ADDON_ATTRIBUTE = "cart_addon";

function isAddonOptedOut(cart) {
  return String(cart?.attributes?.[CART_ADDON_ATTRIBUTE] ?? "") === "0";
}

function syncCartAddonFormValue(on) {
  const value = on ? "1" : "0";
  document.querySelectorAll('input[name="attributes[cart_addon]"]').forEach((input) => {
    input.value = value;
  });
}

function setCartAddonCheckoutBusy(busy) {
  const spinner = `<svg class="cart-checkout-spinner" aria-hidden="true" width="18" height="18" viewBox="0 0 64 64" fill="none" focusable="false"><path fill="currentColor" opacity=".35" d="M23.86 1.05C40.92-3.44 58.46 6.8 62.95 23.86c4.49 17.06-5.74 34.6-22.81 39.09S5.54 57.21 1.05 40.14C-3.44 23.08 6.79 5.54 23.86 1.05zm14.76 56.09c13.86-3.65 22.18-17.89 18.53-31.76-3.65-13.86-17.89-22.18-31.76-18.53S3.21 24.75 6.86 38.61c3.65 13.87 17.89 22.18 31.76 18.53z"/><path fill="currentColor" d="M1.05 40.14l5.8-1.53c3.65 13.87 17.9 22.18 31.76 18.53l1.53 5.8C23.08 67.44 5.54 57.21 1.05 40.14z"/></svg>`;

  document.querySelectorAll("button[name='checkout']").forEach((button) => {
    if (!button.closest("cart-drawer, cart-page, .cart-drawer__footer, .cart-page")) return;
    button.disabled = busy;
    button.toggleAttribute("aria-busy", busy);
    button.querySelector(".cart-checkout-spinner")?.remove();
    if (busy) button.insertAdjacentHTML("beforeend", spinner);
  });
  document.querySelectorAll(
    "cart-drawer .additional-checkout-buttons, cart-page .additional-checkout-buttons, [data-cart-total-price], [data-cart-subtotal-price]"
  ).forEach((el) => {
    el.classList.toggle("is-cart-addon-busy", busy);
  });
}

function preserveCartAddonToggle(root = document) {
  const current = root.querySelector?.("cart-addon-toggle") || document.querySelector("cart-addon-toggle");
  return () => {
    if (!current) return;
    const next = document.querySelector("cart-addon-toggle");
    if (next && next !== current) next.replaceWith(current);
  };
}

async function postCartJson(url, body, { emit = true } = {}) {
  const response = await fetch(`${window.Shopify.routes.root}${url}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify(body),
  });
  return updateCartFromResponse(response, { emit });
}

async function fetchCartJson() {
  return (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
}

function formatCartMoney(cents, withCurrency = false) {
  const settings = window.themeVariables?.settings || {};
  const format = withCurrency
    ? settings.moneyWithCurrencyFormat || settings.moneyFormat
    : settings.moneyFormat || "${{amount}}";
  if (typeof window.Shopify?.formatMoney === "function") {
    return window.Shopify.formatMoney(cents, format);
  }
  const amount = (Number(cents) / 100).toFixed(2);
  return String(format)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
    .replace(/\{\{\s*amount\s*\}\}/g, amount);
}

function applyCartAddonUi(cart) {
  if (!cart) return;

  applyVisibleCartCount(cart);
  LbFreeShippingBar.lastCart = cart;

  const variantId = getCartAddonVariantId();
  const addon = cart.items?.find((item) => Number(item.variant_id) === variantId);
  const optedOut = isAddonOptedOut(cart);

  document.querySelectorAll("cart-addon-toggle").forEach((toggle) => {
    if (addon?.key) toggle.dataset.lineKey = addon.key;
    else delete toggle.dataset.lineKey;
    toggle.toggleAttribute("data-opted-out", optedOut);
  });

  document.querySelectorAll("[data-cart-addon-opt-out]").forEach((el) => {
    el.dataset.cartAddonOptOut = optedOut ? "true" : "false";
  });
  document.querySelectorAll('input[name="attributes[cart_addon]"]').forEach((input) => {
    input.value = cart.attributes?.[CART_ADDON_ATTRIBUTE] ?? "";
  });

  document.querySelectorAll("[data-cart-total-price]").forEach((el) => {
    el.textContent = formatCartMoney(cart.total_price, el.dataset.cartMoney === "with-currency");
  });
  document.querySelectorAll("[data-cart-subtotal-price]").forEach((el) => {
    el.textContent = formatCartMoney(
      cart.items_subtotal_price,
      el.closest("[data-cart-money='with-currency']") != null || Boolean(window.themeVariables?.settings?.currencyCodeEnabled)
    );
  });

  document.querySelectorAll("lb-free-shipping-bar").forEach((bar) => {
    bar.render?.(shippingSubtotalFromCart(cart), { animate: true });
  });
}

function isHiddenCartItem(item) {
  const addonVariantId = getCartAddonVariantId();
  if (addonVariantId && Number(item?.variant_id) === addonVariantId) return true;
  const properties = item?.properties;
  if (properties && !Array.isArray(properties) && properties._Gifted) return true;
  return handleizeType(item?.product_type) === "hidden";
}

let lastVisibleCount = null;

function visibleCartQuantity(cart) {
  if (!Array.isArray(cart?.items)) {
    return lastVisibleCount != null ? lastVisibleCount : Number(cart?.item_count) || 0;
  }
  return cart.items.reduce((sum, item) => {
    if (isHiddenCartItem(item)) return sum;
    return sum + Number(item.quantity || 0);
  }, 0);
}

function paintVisibleCartCount(count) {
  lastVisibleCount = count;
  document.querySelectorAll("cart-count").forEach((el) => {
    el.innerText = String(count);
  });
  document.querySelectorAll("cart-dot").forEach((el) => {
    el.classList.toggle("is-visible", count > 0);
  });
}

function applyVisibleCartCount(cart) {
  if (!cart) return;
  const count = visibleCartQuantity(cart);
  cart.item_count = count;
  paintVisibleCartCount(count);
}

function patchCartCountSetter() {
  const CartCount = customElements.get("cart-count");
  if (!CartCount || CartCount.prototype.__lbVisibleCountPatched) return;
  CartCount.prototype.__lbVisibleCountPatched = true;
  Object.defineProperty(CartCount.prototype, "itemCount", {
    configurable: true,
    set(count) {
      const next = lastVisibleCount != null ? lastVisibleCount : Number(count);
      this.innerText = String(next);
    }
  });
}

patchCartCountSetter();

const initialCartCount = document.querySelector("cart-count");
if (initialCartCount) {
  lastVisibleCount = Number(String(initialCartCount.textContent).trim()) || 0;
}

document.addEventListener(
  "cart:change",
  (event) => {
    const cart = event.detail?.cart;
    if (!cart) return;
    applyVisibleCartCount(cart);
  },
  true
);

async function syncVisibleCartCountFromServer() {
  try {
    const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    applyVisibleCartCount(cart);
  } catch (error) {
    /* ignore */
  }
}

document.addEventListener("cart:refresh", () => {
  syncVisibleCartCountFromServer();
  window.setTimeout(syncVisibleCartCountFromServer, 250);
});

window.addEventListener("pageshow", () => {
  syncVisibleCartCountFromServer();
});

syncVisibleCartCountFromServer();

function bundleCartSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
  );
  return sections;
}

class CartPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.closest(".shopify-section")?.id?.replace(/^shopify-section-/, "");
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener("cart:prepare-bundled-sections", this.onBundle, { signal });
    document.addEventListener("cart:refresh", this.onRefresh, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onBundle = (event) => {
    if (this.sectionId) event.detail.sections.push(this.sectionId);
  };

  onRefresh = async () => {
    if (!this.sectionId) return;
    const html = await (await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`)).text();
    this.replaceFromHtml(html);
  };

  replaceFromHtml(html) {
    const current = this.closest(".shopify-section");
    if (!current) return;
    const restoreAddon = preserveCartAddonToggle(current);
    const doc = new DOMParser().parseFromString(html, "text/html");
    const incoming = doc.getElementById(`shopify-section-${this.sectionId}`) || doc.body;
    current.innerHTML = incoming.innerHTML;
    restoreAddon();
  }
}

if (!customElements.get("cart-page")) {
  customElements.define("cart-page", CartPage);
}

async function updateCartFromResponse(response, { emit = true, baseEvent = "cart:change" } = {}) {
  const responseJson = await response.json();
  if (!response.ok) {
    throw new Error(responseJson.description || "Cart update failed");
  }

  const isFullCart = Boolean(responseJson.token) && Array.isArray(responseJson.items);
  const cartContent = isFullCart
    ? responseJson
    : await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
  if (responseJson.sections) {
    cartContent.sections = responseJson.sections;
  }

  if (emit) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent,
          cart: cartContent,
        },
      })
    );
  }

  return cartContent;
}

class CartAddonToggle extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    if (!this.input) return;
    this.onChange = this.onChange.bind(this);
    this.input.addEventListener("change", this.onChange);
  }

  disconnectedCallback() {
    this.input?.removeEventListener("change", this.onChange);
  }

  async onChange() {
    if (this.busy) return;

    const wanted = this.input.checked;
    syncCartAddonFormValue(wanted);
    this.busy = true;
    this.style.pointerEvents = "none";
    setCartAddonCheckoutBusy(true);

    try {
      const variantId = Number(this.dataset.variantId);

      if (wanted) {
        await postCartJson("cart/update.js", { attributes: { [CART_ADDON_ATTRIBUTE]: "1" } }, { emit: false });
        await postCartJson(
          "cart/add.js",
          { items: [{ id: variantId, quantity: 1 }] },
          { emit: false }
        );
      } else {
        await removeCartAddonByVariant(variantId);
      }

      applyCartAddonUi(await fetchCartJson());
    } catch (error) {
      if (this.isConnected && this.input) {
        this.input.checked = !wanted;
        syncCartAddonFormValue(!wanted);
      }
    } finally {
      this.busy = false;
      this.style.pointerEvents = "";
      setCartAddonCheckoutBusy(false);
    }
  }
}

if (!customElements.get("cart-addon-toggle")) {
  customElements.define("cart-addon-toggle", CartAddonToggle);
}

document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="attributes[cart_addon]"]')) return;
    if ([...document.querySelectorAll("cart-addon-toggle")].some((el) => el.busy)) {
      event.preventDefault();
      return;
    }
    const on = Boolean(document.querySelector("cart-addon-toggle input[type='checkbox']")?.checked);
    form.querySelectorAll('input[name="attributes[cart_addon]"]').forEach((input) => {
      input.value = on ? "1" : "0";
    });
  },
  true
);

let syncingCartAddon = false;

function resetCartAddonToggles() {
  document.querySelectorAll("cart-addon-toggle").forEach((el) => {
    el.busy = false;
    el.style.pointerEvents = "";
  });
  setCartAddonCheckoutBusy(false);
}

async function removeCartAddonByVariant(variantId) {
  const cart = await fetchCartJson();
  const addon = cart.items?.find((item) => Number(item.variant_id) === Number(variantId));
  const payload = { attributes: { [CART_ADDON_ATTRIBUTE]: "0" } };
  if (addon?.key) payload.updates = { [addon.key]: 0 };
  await postCartJson("cart/update.js", payload, { emit: false });
}

async function removeCartAddonLine(addon, sections) {
  const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: addon.key,
      quantity: 0,
      sections: sections.join(","),
      sections_url: window.location.pathname,
    }),
  });
  await updateCartFromResponse(response);
}

document.addEventListener("cart:change", async (event) => {
  if (syncingCartAddon) return;
  if (event.detail?.baseEvent === "cart-addon:change") return;

  const cart = event.detail?.cart;
  const variantId = getCartAddonVariantId();
  if (!cart?.token || !Array.isArray(cart.items) || !variantId) return;

  const others = cart.items.filter((item) => item.variant_id !== variantId);
  const addon = cart.items.find((item) => item.variant_id === variantId);
  const shouldRemove = Boolean(addon) && (others.length === 0 || isAddonOptedOut(cart));
  if (!shouldRemove) return;

  syncingCartAddon = true;
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

  try {
    if (others.length === 0) {
      await removeCartAddonLine(addon, bundleCartSections());
    } else {
      await removeCartAddonByVariant(variantId);
      applyCartAddonUi(await fetchCartJson());
    }
  } finally {
    syncingCartAddon = false;
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  }
});

document.addEventListener("cart:refresh", () => {
  window.__lbRestoreCartAddon = preserveCartAddonToggle();
}, true);

document.addEventListener("cart-drawer:refreshed", () => {
  window.__lbRestoreCartAddon?.();
  window.__lbRestoreCartAddon = null;
  resetCartAddonToggles();
});

window.addEventListener("pageshow", () => {
  resetCartAddonToggles();
});

(async () => {
  if (!document.querySelector("cart-addon-toggle")) return;
  try {
    const cart = await fetchCartJson();
    const variantId = getCartAddonVariantId();
    const addon = cart.items?.find((item) => Number(item.variant_id) === Number(variantId));
    if (!isAddonOptedOut(cart) || !addon) return;
    await removeCartAddonByVariant(variantId);
    applyCartAddonUi(await fetchCartJson());
  } catch (error) {
    /* ignore */
  }
})();

function initThemeEnhancements(root = document) {
  initThemeSliders(root);
  initJudgemeCardBadges(root);
  initCollectionHighlight(root);
  initCollectionCardReveal(root);
  initHeroSlider(root);
  initBlogTags(root);
  initAboutCollectionBooks(root);
}

initThemeEnhancements();

document.addEventListener("variant:change", (event) => {
  updateAboutCollectionIsbn(event.detail?.variant);
  updateAboutCollectionShopifySpecs(event.detail?.variant);
});

document.addEventListener("shopify:section:load", (event) => {
  initThemeEnhancements(event.target);
});

document.addEventListener("shopify:block:select", (event) => {
  const heroSlide = event.target.closest("[data-hero-slide]");
  if (heroSlide) {
    activateHeroSlider(heroSlide.closest("[data-hero-slider]"), heroSlide.dataset.heroSlide);
    return;
  }

  const tab =
    event.target.closest("[data-highlight-tab]") ||
    event.target.querySelector("[data-highlight-tab]");
  if (!tab) return;
  activateCollectionHighlight(tab.closest("[data-collection-highlight]"), tab.dataset.highlightTab);
});

function cartQtyAlertHost(el) {
  return el.closest("cart-drawer, cart-page, .shopify-section--main-cart");
}

function friendlyCartQtyMessage() {
  return (
    window.themeVariables?.strings?.quantityUnavailable ||
    "Sorry — there isn’t enough of this item in stock. We put the quantity back."
  );
}

function visibleLineQuantity(fromEl) {
  const row = fromEl?.closest("tr");
  const inCartRow = row?.querySelector("td .v-stack > line-item-quantity") || row?.querySelector("td line-item-quantity");
  return (
    inCartRow ||
    fromEl?.closest("line-item-quantity") ||
    fromEl?.closest("line-item") ||
    document.querySelector("cart-page") ||
    document.getElementById("cart-drawer")
  );
}

function appendCartQtyAlert(mount, alert) {
  const next = mount.nextElementSibling;
  if (next?.matches?.(".cart-qty-alert, .cart-qty-alert-wrap")) next.remove();
  mount.querySelectorAll(".cart-qty-alert").forEach((el) => el.remove());
  mount.querySelectorAll(".cart-qty-alert-wrap").forEach((el) => el.remove());

  if (mount.closest?.(".shopify-section--main-cart") || mount.matches?.(".shopify-section--main-cart")) {
    const wrap = document.createElement("div");
    wrap.className = "cart-qty-alert-wrap";
    wrap.append(alert);
    mount.append(wrap);
    return;
  }

  mount.insertAdjacentElement("afterend", alert);
}

function showCartQtyAlert(fromEl) {
  const qty = fromEl?.closest("line-item-quantity") || visibleLineQuantity(fromEl);
  if (!qty) return;

  const inCartPage = Boolean(qty.closest(".shopify-section--main-cart"));
  const mount = inCartPage ? qty.closest(".v-stack") || qty : qty;
  const alert = document.createElement("p");
  alert.className = "cart-qty-alert";
  alert.setAttribute("role", "alert");
  alert.textContent = friendlyCartQtyMessage();
  appendCartQtyAlert(mount, alert);

  qty.querySelector("quantity-selector")?.restoreDefaultValue?.();
}

function restyleCartQtyAlert(el) {
  if (!(el instanceof HTMLElement) || el.dataset.qtyAlertDone === "true") return;
  if (!cartQtyAlertHost(el)) return;
  if (!el.closest("line-item, line-item-quantity, .v-stack")) return;

  el.dataset.qtyAlertDone = "true";
  el.classList.add("cart-qty-alert");
  const svg = el.querySelector("svg");
  el.replaceChildren();
  if (svg) el.append(svg);
  el.append(document.createTextNode(` ${friendlyCartQtyMessage()}`));

  const qty = el.closest("line-item")?.querySelector("line-item-quantity") || visibleLineQuantity(el);
  if (!qty) return;

  if (qty.closest(".shopify-section--main-cart")) {
    const mount = qty.closest(".v-stack") || qty;
    if (!el.parentElement?.classList.contains("cart-qty-alert-wrap")) appendCartQtyAlert(mount, el);
    return;
  }

  if (el.previousElementSibling !== qty) qty.insertAdjacentElement("afterend", el);
}

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.('[role="alert"]')) restyleCartQtyAlert(node);
      node.querySelectorAll?.('[role="alert"]').forEach(restyleCartQtyAlert);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
